import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment } from "react";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "../../base/input/input";
import { cn } from "../../../utils";
import { DragHandle } from "../drag-handle";
import { getRuleInputValue } from "./decisionTableColumns";
// Cycle through example placeholders by [ruleIndex % 2][colIndex % 2]
const CONDITION_PLACEHOLDER_KEYS = [
    [
        "viralRecording.placeholders.enterCondition",
        "viralRecording.placeholders.enterCondition2"
    ],
    [
        "viralRecording.placeholders.enterConditionAlt",
        "viralRecording.placeholders.enterCondition2Alt"
    ]
];
const OUTPUT_PLACEHOLDER_KEYS = [
    "viralRecording.placeholders.enterOutput",
    "viralRecording.placeholders.enterOutput2"
];
export function DecisionTableRow({ rule, ruleIndex, columns, allRules, invalidRules, canDelete, onUpdateRuleInput, onUpdateRuleOutput, onDeleteRule }) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: rule.clientId
    });
    return /*#__PURE__*/ _jsxs("tr", {
        ref: setNodeRef,
        style: {
            transform: CSS.Transform.toString(transform),
            transition
        },
        className: cn("transition-colors hover:bg-surface-subtle/50", ruleIndex % 2 === 0 ? "bg-white" : "bg-surface-subtle/30", isDragging && "relative z-10 opacity-90 shadow-lg"),
        children: [
            /*#__PURE__*/ _jsx("td", {
                className: "w-8 px-1 py-3",
                children: /*#__PURE__*/ _jsx(DragHandle, {
                    label: t("viralRecording.buttons.reorderRule"),
                    setActivatorNodeRef: setActivatorNodeRef,
                    attributes: attributes,
                    listeners: listeners,
                    className: "p-1 text-fg-subtle hover:text-fg-secondary hover:bg-muted"
                })
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-4 py-3 text-sm font-medium text-foreground",
                children: ruleIndex + 1
            }),
            columns.map((column, colIndex)=>{
                const isRuleInvalid = invalidRules.has(ruleIndex);
                const allInputsEmpty = !rule.inputs.some((input)=>input.value?.trim());
                const hasInputError = isRuleInvalid && allInputsEmpty;
                return /*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("td", {
                            className: "px-4 py-3",
                            children: /*#__PURE__*/ _jsx(Input, {
                                value: getRuleInputValue(rule.inputs, column.name) ?? "",
                                onChange: (e)=>onUpdateRuleInput(ruleIndex, column.name, e.target.value),
                                placeholder: t(CONDITION_PLACEHOLDER_KEYS[ruleIndex % 2][colIndex % 2]),
                                className: cn("w-full", hasInputError && "border-destructive")
                            })
                        }),
                        /*#__PURE__*/ _jsxs("td", {
                            className: "relative w-8 px-0",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "absolute inset-y-0 w-px bg-surface-strong left-1/2"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "absolute inset-0 flex items-center justify-center",
                                    children: /*#__PURE__*/ _jsx("span", {
                                        className: cn("relative z-10 px-1 text-xs font-semibold text-fg-subtle rounded-sm", ruleIndex % 2 === 0 ? "bg-white" : "bg-surface-subtle"),
                                        children: colIndex < columns.length - 1 ? "&" : "="
                                    })
                                })
                            ]
                        })
                    ]
                }, column.id);
            }),
            /*#__PURE__*/ _jsxs("td", {
                className: "px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(Input, {
                        value: rule.output || "",
                        onChange: (e)=>onUpdateRuleOutput(ruleIndex, e.target.value),
                        placeholder: t(OUTPUT_PLACEHOLDER_KEYS[ruleIndex % 2]),
                        list: `output-suggestions-${ruleIndex}`,
                        className: cn("w-full", invalidRules.has(ruleIndex) && !rule.output?.trim() && "border-destructive")
                    }),
                    /*#__PURE__*/ _jsx("datalist", {
                        id: `output-suggestions-${ruleIndex}`,
                        children: Array.from(new Set(allRules.map((r)=>r.output).filter((o)=>o && o.trim()))).filter((output)=>output !== rule.output).map((output)=>/*#__PURE__*/ _jsx("option", {
                                value: output
                            }, output))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("td", {
                className: "px-4 py-3",
                children: /*#__PURE__*/ _jsx("button", {
                    onClick: ()=>onDeleteRule(ruleIndex),
                    className: "p-1.5 text-danger hover:bg-danger-surface rounded-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
                    disabled: !canDelete,
                    title: t("viralRecording.buttons.delete"),
                    children: /*#__PURE__*/ _jsx(Trash2, {
                        size: 16
                    })
                })
            })
        ]
    });
}

//# sourceMappingURL=DecisionTableRow.js.map